import { useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/lib/firebase";

// Reads injected HTML/scripts from siteSettings/global (editable in the admin panel) and
// injects them into the document head and body at runtime.
// Renders nothing. Mounted on the public site only.

const INJECT_ATTR = "data-injected-by";
const INJECT_VALUE = "meproperty-code-injection";

function cleanInjectedElements(container) {
  const elements = container.querySelectorAll(`[${INJECT_ATTR}="${INJECT_VALUE}"]`);
  elements.forEach((el) => el.remove());
}

function injectHtml(htmlString, container) {
  if (!htmlString || !htmlString.trim()) return;

  try {
    const parser = new DOMParser();
    const parsed = parser.parseFromString(htmlString, "text/html");
    
    // Gather nodes from both head and body of the parsed document
    const nodesToInject = [];
    parsed.head.childNodes.forEach((node) => nodesToInject.push(node));
    parsed.body.childNodes.forEach((node) => nodesToInject.push(node));

    nodesToInject.forEach((node) => {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node;
        let newEl;
        
        if (el.tagName === "SCRIPT") {
          newEl = document.createElement("script");
          // Copy all attributes
          Array.from(el.attributes).forEach((attr) => {
            newEl.setAttribute(attr.name, attr.value);
          });
          newEl.text = el.text; // Copy script contents
        } else {
          newEl = el.cloneNode(true);
        }
        
        newEl.setAttribute(INJECT_ATTR, INJECT_VALUE);
        container.appendChild(newEl);
      }
    });
  } catch (err) {
    console.error("Failed to inject custom scripts:", err);
  }
}

export default function SiteTracking() {
  useEffect(() => {
    let cancelled = false;
    getDoc(doc(db, "siteSettings", "global"))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const s = snap.data();
        
        // Clean up previously injected elements
        cleanInjectedElements(document.head);
        cleanInjectedElements(document.body);

        if (s.headerScripts) {
          injectHtml(s.headerScripts, document.head);
        }
        if (s.bodyScripts) {
          injectHtml(s.bodyScripts, document.body);
        }
      })
      .catch((err) => {
        console.error("Error loading code injections:", err);
      });
      
    return () => {
      cancelled = true;
    };
  }, []);

  return null;
}

